import { ApplicationElement, html } from '../application_element/element.js';
import { ref, createRef } from 'lit/directives/ref.js';
import style from './style.lit.css';

import { IntersectionController } from '@lit-labs/observers/intersection-controller.js';
import { ResizeController } from '@lit-labs/observers/resize-controller.js';

import throttle from 'lodash-es/throttle.js';
import debounce from 'lodash-es/debounce.js';

import { nextFrame } from '../../lib/helpers.js';

/**
 * If the loaded content is less then intersection observer it will not work.
 *
 * @prop edgeThresholdPercent - How much pixels near bottom of scroll we consider as near end
 */
export default class Scroller extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    edgeThresholdPercent: { type: Number },
    disableNearTopDetector: { type: Boolean },
  };

  resizeObserver = new ResizeController(this, {
    skipInitial: true,
    callback: () => this.debouncedScrollToEndIfNearEnd(),
  });

  topIntersector = new IntersectionController(this, {
    target: null,
    // Take last entry as if you revisit same page with turbolinks
    // there could be multiple entries
    callback: entries => entries.at(-1).isIntersecting,
  });

  scrollElementRef = createRef();

  topElementRef = createRef();

  debouncedScrollToEndIfNearEnd = debounce(this.scrollToEndIfNearEnd, 32);

  throttledHandleScroll = throttle(this.handleScroll, 75);

  constructor() {
    super();
    this.previous = this.current;
    this.edgeThresholdPercent = 10;
    this.disableNearTopDetector = false;
  }

  firstUpdated() {
    this.topIntersector.observe(this.topElement);
  }

  get topElement() {
    return this.topElementRef.value;
  }

  get scrollElement() {
    return this.scrollElementRef.value;
  }

  /** @internal */
  get maxScrollTop() {
    return this.scrollElement?.scrollHeight - this.scrollElement?.clientHeight;
  }

  /** @internal */
  // get scrollNearTop() {
  //   const { scrollPercentage, edgeThresholdPercent } = this;
  //   return scrollPercentage <= edgeThresholdPercent;
  // }

  /** @internal */
  get scrollNearBottom() {
    const { scrollPercentage, edgeThresholdPercent } = this;
    return scrollPercentage >= 100 - edgeThresholdPercent;
  }

  /** @internal */
  get scrollAtBottom() {
    return this.scrollPercentage >= 100;
  }

  get scrollPercentage() {
    const { maxScrollTop, scrollElement } = this;
    return Math.round((scrollElement?.scrollTop / maxScrollTop) * 100);
  }

  // Another updated happens between start and end and that triggers case 3
  async scrollToEnd() {
    await nextFrame();
    this.scrollElement?.scrollTo({
      top: this.scrollElement?.scrollHeight,
      behavior: 'instant',
    });

    return true;
  }

  scrollToTop(behavior = 'instant') {
    this.scrollElement.scrollTo({ top: 0, behavior });
  }

  scrollToEndIfNearEnd() {
    if (this.scrollAtBottom) return true;

    return this.scrollNearBottom && this.scrollToEnd();
  }

  // 2 Frames

  get current() {
    const { scrollHeight, scrollTop } = this.scrollElement || {};
    return {
      scrollHeight,
      scrollTop,
    };
  }

  saveScrollPosition() {
    this.previous = this.current;
    this.positionSaved = true;
  }

  async restoreScrollPosition() {
    await nextFrame();
    this.instantRestoreScrollPosition();

    return true;
  }

  instantRestoreScrollPosition() {
    const { previous, current, positionSaved, scrollElement } = this;
    if (!positionSaved || !scrollElement) return;

    const scrollHeightDiff = current.scrollHeight - previous.scrollHeight;

    const newTop = previous.scrollTop + scrollHeightDiff;
    scrollElement.scrollTo({ top: newTop, behavior: 'instant' });

    this.positionSaved = false;
  }

  handleScroll() {
    this.dispatchEvent(new Event('scroller-active'));
    if (this.shouldDispatchScrollNearTop) {
      this.dispatchEvent(
        new Event('scroller-near-top', { bubbles: true, composed: true })
      );
    }
  }

  get shouldDispatchScrollNearTop() {
    const isIntersecting = this.topIntersector.value;
    const { disableNearTopDetector, scrollPercentage } = this;
    return !disableNearTopDetector && isIntersecting && scrollPercentage <= 50;
  }

  onScroll() {
    this.throttledHandleScroll();
  }

  render() {
    return html`
      <main @scroll=${this.onScroll} ${ref(this.scrollElementRef)}>
        <header
          ${ref(this.topElementRef)}
          .hidden=${this.disableNearTopDetector}></header>
        <div class="content">
          <slot></slot>
        </div>
      </main>
    `;
  }
}

customElements.get('ef-scroller') ||
  customElements.define('ef-scroller', Scroller);
